import { ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import DeleteButton from '@/components/ui/DeleteButton'
import { cn } from '@/lib/utils'
import type Product from '@/types/serializers/Product'

interface ProductListItemProps {
  product: Product
  canManage: boolean
  selected: boolean
  onSelect: (product: Product) => void
  onDelete: (product: Product) => void
}

export function ProductListItem({ product, canManage, selected, onSelect, onDelete }: ProductListItemProps) {
  return (
    <div
      role={canManage ? 'button' : undefined}
      tabIndex={canManage ? 0 : undefined}
      onClick={canManage ? () => onSelect(product) : undefined}
      onKeyDown={canManage ? (e) => (e.key === 'Enter' || e.key === ' ') && onSelect(product) : undefined}
      className={cn(
        'flex items-center justify-between rounded-lg border p-3',
        canManage && 'cursor-pointer hover:border-primary/50',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border'
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {product.logo_url ? (
          <img src={product.logo_url} alt="" className="size-10 rounded object-cover shrink-0" />
        ) : (
          <span
            className="size-10 rounded shrink-0 border border-border"
            style={{ backgroundColor: product.primary_color || '#10b981' }}
            aria-hidden
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{product.name}</p>
            {product.is_default && <Badge variant="secondary">Default</Badge>}
            {!product.active && <Badge variant="outline">Inactive</Badge>}
          </div>
          {product.header_name && (
            <p className="text-xs text-muted-foreground truncate">{product.header_name}</p>
          )}
        </div>
      </div>

      {canManage && (
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {!product.is_default && <DeleteButton onDelete={() => onDelete(product)} />}
          <ChevronRight className={cn('size-4 text-muted-foreground transition-transform', selected && 'rotate-90')} />
        </div>
      )}
    </div>
  )
}
